import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    ChevronDown,
    PlusCircle,
    Briefcase,
    LayoutDashboard,
    Search
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Workspace, useWorkspace } from '../hooks/useWorkspace';

export const WorkspaceSwitcher: React.FC = () => {
    const { workspaces, activeWorkspace, setActiveWorkspaceId } = useWorkspace();
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const navigate = useNavigate();

    const filteredWorkspaces = workspaces.filter(ws =>
        ws.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="relative">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-3 px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl transition-all group"
            >
                <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center text-primary group-hover:bg-primary/30 transition-colors">
                    <Briefcase size={18} />
                </div>
                <div className="hidden sm:block text-left">
                    <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest leading-none mb-1">Active Brand</p>
                    <p className="text-sm font-bold truncate max-w-[120px]">{activeWorkspace?.name || 'Select Brand'}</p>
                </div>
                <ChevronDown size={14} className={`text-gray-500 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            <AnimatePresence>
                {isOpen && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setIsOpen(false)}
                            className="fixed inset-0 z-40"
                        />
                        <motion.div
                            initial={{ opacity: 0, y: 10, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 10, scale: 0.95 }}
                            className="absolute top-full left-0 mt-3 w-72 bg-[#0c0c0c] border border-white/10 rounded-2xl shadow-2xl p-3 z-50 overflow-hidden"
                        >
                            <div className="mb-3 relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={14} />
                                <input
                                    type="text"
                                    placeholder="Search brands..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full bg-white/5 border border-white/10 rounded-lg py-2 pl-9 pr-3 text-sm focus:outline-none focus:border-primary/50 transition-colors"
                                />
                            </div>

                            <div className="max-h-64 overflow-y-auto space-y-1 custom-scrollbar">
                                {filteredWorkspaces.map((ws) => (
                                    <button
                                        key={ws.id}
                                        onClick={() => {
                                            setActiveWorkspaceId(ws.id);
                                            setIsOpen(false);
                                        }}
                                        className={`w-full text-left p-3 rounded-xl flex items-center gap-3 transition-all ${ws.id === activeWorkspace?.id
                                                ? 'bg-primary/10 text-primary border border-primary/20'
                                                : 'hover:bg-white/5 text-gray-300 border border-transparent'
                                            }`}
                                    >
                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${ws.id === activeWorkspace?.id ? 'bg-primary/20' : 'bg-white/5'}`}>
                                            <LayoutDashboard size={14} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-bold truncate">{ws.name}</p>
                                            <p className="text-[10px] text-gray-500 truncate">
                                                {Object.keys(ws.onboardingStatus || {}).length - 1} platforms connected
                                            </p>
                                        </div>
                                        {ws.id === activeWorkspace?.id && (
                                            <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                                        )}
                                    </button>
                                ))}
                            </div>

                            <div className="h-px bg-white/5 my-3" />

                            <button
                                onClick={() => {
                                    navigate('/onboarding?mode=create');
                                    setIsOpen(false);
                                }}
                                className="w-full flex items-center gap-3 p-3 rounded-xl bg-primary text-black hover:bg-primary/90 transition-all font-bold text-sm shadow-lg shadow-primary/20"
                            >
                                <PlusCircle size={18} />
                                Add New Brand
                            </button>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </div>
    );
};
